import styled from "@emotion/styled";
import { FlexRow, Text, ScreenReaderText, ContentToggle } from "atoms";
import { formatDate } from "utils/date";
import {
  ReviewAvatar,
  ReviewRatingIcon,
  ReviewRatingText,
  ReviewUsername,
} from "./Review";
import { RatingIcon } from "./RatingIcon";
import type { ArtistReview, ProjectReview } from "../types";

type UsersReviewProps = {
  review?: ArtistReview | ProjectReview | null;
};

function UsersReview({ review }: UsersReviewProps) {
  if (!review) return null;

  const {
    createdAt,
    displayAvatar,
    displayName,
    forYouIf,
    isAnonymous,
    notForYouIf,
    status,
    rating,
    updatedAt,
  } = review;

  return (
    <Wrapper>
      <Header
        flexWrap="nowrap"
        alignItems="flex-start"
        itemsFlex="0 1 auto"
        gap="6px 1rem"
      >
        <ReviewAvatar
          src={isAnonymous ? undefined : displayAvatar}
          alt={`Profile picture for ${
            isAnonymous ? "anonymous reviewer" : displayName
          }`}
          loading="lazy"
        />
        <FlexRow flexWrap="wrap">
          <HeaderText>
            <ReviewUsername size="xs" bold>
              Your Review
            </ReviewUsername>
            {isAnonymous && (
              <SText as="div" size="xxxs" textTransform="capitalize">
                (anonymous)
              </SText>
            )}
            <SText as="div" size="xxxs" textTransform="capitalize">
              {status.toLowerCase()}
            </SText>
          </HeaderText>
        </FlexRow>
      </Header>

      <Body>
        <ContentToggle showLabel="Show review" hideLabel="Hide review">
          <Text size="xxxs">
            Created on{" "}
            {formatDate(createdAt, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
            {updatedAt && (
              <>
                <br />
                Updated on
                {formatDate(updatedAt, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </>
            )}
          </Text>

          <Rating>
            <ReviewRatingIcon>
              <RatingIcon review={review} aria-label="hidden" />
            </ReviewRatingIcon>
            <ReviewRatingText size="xxs">
              {rating ? (
                <>
                  <ScreenReaderText as="span">Your rating:</ScreenReaderText>
                  {rating}
                </>
              ) : (
                <em>No Rating</em>
              )}
            </ReviewRatingText>
          </Rating>

          {forYouIf && (
            <Text size="xxs">
              <strong>For you if:</strong> {forYouIf}
            </Text>
          )}
          {notForYouIf && (
            <Text size="xxs">
              <strong>Not for you if:</strong> {notForYouIf}
            </Text>
          )}
        </ContentToggle>
      </Body>
    </Wrapper>
  );
}

const Wrapper = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.bg,
  borderRadius: theme.borderRadius.md,
  boxShadow: theme.boxShadow.light,
  padding: "1rem",
  marginBottom: "1rem",
}));

const Rating = styled(FlexRow)({
  gap: 6,
  marginBottom: 10,
  p: {
    margin: 0,
  },
  "&>*": {
    transform: "none",
  },
});

const Header = styled(FlexRow)({
  gap: 10,
});

const HeaderText = styled.div({
  "&>*": {
    margin: 0,
    transform: "none !important",
  },
});

const Body = styled.div({
  margin: ".5rem 0 0",
  p: {
    marginBottom: ".5em",
  },
});

const SText = styled(Text)({
  lineHeight: 1.1,
  margin: 0,
  "&:first-of-type": {
    marginTop: 6,
  },
});

export { UsersReview };
